import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {NavigationStart, Router, RouterEvent} from '@angular/router';
import {MatLegacyDialog as MatDialog} from '@angular/material/legacy-dialog';
import Driver from 'driver.js';
import {select, Store} from '@ngrx/store';
import {Observable, of as observableOf, Subject} from 'rxjs';
import {filter, take, tap, map, switchMap} from 'rxjs/operators';

import {AuthenticatedUserService} from '@app/core/service/authenticated-user.service';
import {UserPreferencesService} from '@app/core/service/user-preferences.service';
import {PreferenceItem, Preferences} from '@app/core/model/user-preferences.model';
import {getAuthenticatedUserPreferences, State} from '@app/app.reducers';
import {AuthenticatedUpdateExtraUserPreferencesAction} from '@app/views/common/auth.actions';
import {environment} from '@env/environment';
import {CustomerService} from '@app/core/service/customer.service';

const ProviderToursVisibility = new Map<string, Map<number, boolean>>([
    [
        'administrationPage',
        new Map<number, boolean>([
            [
                1, // index of element
                !CustomerService.checkIsAmplifyCustomer() // visibility rule
            ],
            [
                3,
                !CustomerService.checkIsAmplifyCustomer()
            ]
        ])
    ]
]);

const VendorToursVisibility = new Map<string, Map<number, boolean>>([
]);

@Injectable()
export class InteractiveGuideService {

    private steps = [];
    private driverOptions = {
        className: 'custom-popover-style',
        animate: false,
        padding: 4,
        allowClose: true,
        overlayClickNext: false,
        nextBtnText: 'Next',
        prevBtnText: 'Previous',
        // scrollIntoViewOptions: { behavior: 'auto' as 'auto', block: 'center' as 'center', inline: 'center' as 'center'},
        onHighlightStarted: ((Element) => {
            document.getElementsByClassName('menu-side-nav')[0].setAttribute('style', 'overflow: hidden;');

            if (Element.options.element && Element.options.element.includes('tab-header')) {
                const lastUnderscoreIndex = String(Element.options.element).indexOf('-tab-header');
                const tabLabel = String(Element.options.element).substring(1, lastUnderscoreIndex).split('-').join(' ').toLocaleUpperCase();
                this.toggleTab(tabLabel);
            }

            const highlightedElement: HTMLElement = document.querySelector(Element.options.element);

            if (highlightedElement && highlightedElement.getBoundingClientRect().top < 64 ) {
                const stepIndex = this.driver.getSteps().findIndex(step => step['options'] && step['options'].element == Element.options.element);
                document.querySelector(Element.options.element).scrollIntoView({block: 'center'});
                setTimeout(() => {
                    window.dispatchEvent(new Event('resize'));
                }, 0);
            }
        }),
        onHighlighted: ((Element) => {
            this.checkElementPositionInterval = setInterval(() => {
                const highlightedElement: Element = document.querySelector(Element.options.element);
                const stageElement = document.getElementById('driver-highlighted-element-stage');


                if (stageElement && highlightedElement) {
                    const stagePosition = stageElement.getBoundingClientRect();
                    const highlightedElementPosition = highlightedElement.getBoundingClientRect();

                    const incorrectStagePosition = highlightedElementPosition.top <= (stagePosition.top - 10) ||
                        highlightedElementPosition.right >= (stagePosition.right + 10) ||
                        highlightedElementPosition.top <= (stagePosition.top - 20) ||
                        highlightedElementPosition.left <= (stagePosition.left - 10);

                    if (incorrectStagePosition) {
                        window.dispatchEvent(new Event('resize'));
                    }
                }
            }, 500);
        }),
        onDeselected: () => {
            window.clearInterval(this.checkElementPositionInterval);
        },
        onPrevious: (Element) => {
            if (Element.options.element) {
                const stepIndex = this.driver.getSteps().findIndex(step => step['options'] && step['options'].element == Element.options.element);

                if (stepIndex > 1 && this.driver.getSteps()[stepIndex - 1]['options'].element === '#add-fab-button') {
                    const tabStepIndex = stepIndex - 2;
                    const activeTabSelector = this.steps[tabStepIndex]['element'];
                    const lastUnderscoreIndex = String(activeTabSelector).indexOf('-tab-header');
                    const tabLabel = String(activeTabSelector).substring(1, lastUnderscoreIndex).split('-').join(' ').toLocaleUpperCase();
                    this.toggleTab(tabLabel);
                }
            }
        },
        onNext: (Element) => {
            window.clearInterval(this.checkElementPositionInterval);
            if (!this.driver.hasNextStep()) {
                this.driver.preventMove();
                this.driver.defineSteps(this.steps);
                this.driver.moveNext();
            }
        },
        onReset: (Element) => {
            window.clearInterval(this.checkElementPositionInterval);
            this.isTourActive = false;
            this.toggleTab('FINISH');
            document.getElementsByClassName('menu-side-nav')[0].setAttribute('style', 'overflow: auto;');
        }
    };
    private driver = new Driver(this.driverOptions);
    private completedInteractiveTours: PreferenceItem[] = [];
    private isTourActive = false;
    get isTourActivePublic() {
        return this.isTourActive;
    }
    private isUserPreferencesReceived = false;
    private IS_AMPLIFY_CUSTOMER = CustomerService.checkIsAmplifyCustomer();

    private checkElementPositionInterval;
    private activeTab = new Subject<any>();
    toggleTab(tabLabel: string) {
        this.activeTab.next(tabLabel);
    }
    getActiveTab(): Observable<string> {
        return this.activeTab.asObservable();
    }

    private _interactiveTours = {
        vendorInteractiveTours: null,
        providerInteractiveTours: null
    };
    get interactiveTours() {
        if (this.authenticatedUserService.isProvider(null)) {
            return this._interactiveTours.providerInteractiveTours;
        } else if (this.authenticatedUserService.isVendor(null)) {
            return this._interactiveTours.vendorInteractiveTours;
        } else {
            return null;
        }
    }
    set interactiveTours(tours) {
        if (this.authenticatedUserService.isProvider(null)) {
            this._interactiveTours.providerInteractiveTours = tours;
        } else if (this.authenticatedUserService.isVendor(null)) {
            this._interactiveTours.vendorInteractiveTours = tours;
        }
    }

    private startTour = new Subject<ITour>();
    public startTour$ = this.startTour.asObservable();

    private openWelcomeDialog = new Subject<boolean>();
    public openWelcomeDialog$ = this.openWelcomeDialog.asObservable();

    private currentPageTour = new Subject<string>();
    public currentPageTour$ = this.currentPageTour.asObservable();

    constructor(
        private http: HttpClient,
        private authenticatedUserService: AuthenticatedUserService,
        private userPreferencesService: UserPreferencesService,
        private customerService: CustomerService,
        private router: Router,
        private matDialog: MatDialog,
        private store: Store<State>
    ) {
        this.store.pipe(
            select(getAuthenticatedUserPreferences),
            filter(preferences => {
                this.isUserPreferencesReceived = !!preferences;
                return !!preferences;
            })
        ).subscribe((preferences: Preferences) => {
            if (preferences && Object(preferences).hasOwnProperty('completedInteractiveTours')) {
                this.completedInteractiveTours = preferences.completedInteractiveTours;
            }
        });

        this.authenticatedUserService.authenticatedUser$.pipe(filter(user => !user)).subscribe(
            () => {
                this.completedInteractiveTours = [];
                this.isTourActive = false;
                this.isUserPreferencesReceived = false;
            }
        );

        this.router.events
            .pipe(filter((event: RouterEvent) => event instanceof NavigationStart))
            .subscribe((event) => {
                this.currentPageTour.next('');
                this.stop();
            }
            );

        this.startTour$.pipe(
            tap((tour: ITour) => {
                if (tour.tourName !== 'syncHistoryPage' ) {
                    this.currentPageTour.next(tour.tourName);
                }
            }),
            filter(() => !!this.authenticatedUserService.authenticatedUser && !this.isTourActive),
            switchMap((tour: ITour) => {
                if (tour.tourName == 'welcomeTour' || this.interactiveTours && (this.isInteractiveTourAllowed() || tour.forcedStart)) {
                    this.isTourActive = this.isInteractiveTourAllowed() || tour.forcedStart;

                    return observableOf(tour);
                } else if (this.isInteractiveTourAllowed() || tour.forcedStart) {
                    return this.store.pipe(
                        select(getAuthenticatedUserPreferences),
                        filter(preferences => !!preferences),
                        take(1),
                        switchMap(preferences => {
                            if (Object(preferences).hasOwnProperty('completedInteractiveTours')) {
                                this.completedInteractiveTours = preferences.completedInteractiveTours;
                            }

                            if (tour.tourName == 'welcomeTour') {
                                this.isTourActive = this.isInteractiveTourNotCompleted('welcomeTour') && this.isWelcomeTourAllowed();

                                return observableOf(tour);
                            } else if (tour.tourName) {
                                this.isTourActive = this.isInteractiveTourAllowed() || tour.forcedStart;

                                return this.getDefaultInteractiveTours().pipe(
                                    take(1),
                                    tap((defaultTours: Object) => this.interactiveTours = defaultTours),
                                    map(() => tour)
                                );
                            } else {
                                return observableOf(null);
                            }
                        })
                    );
                } else {
                    return observableOf(null);
                }
            }),
            filter(tour => !!tour)
        ).subscribe( (tour: ITour) => {
            if (tour.tourName == 'welcomeTour') {
                this.startWelcomeTour();
            } else if (this.isInteractiveTourAllowed() || tour.forcedStart) {
                this.startTourByName(tour.tourName, tour.forcedStart);
            } else {
                this.isTourActive = false;
            }
        }, error => {
            this.isTourActive = false;
        });
    }

    start(tourName: string, forcedStart: boolean = false) {
        this.startTour.next({tourName: tourName, forcedStart: forcedStart});
    }

    isInteractiveTourAllowed(): boolean {
        return this.isUserPreferencesReceived && this.completedInteractiveTours.findIndex((preferenceItem: PreferenceItem) => preferenceItem.selectedId == 'welcomeTour' && preferenceItem.selectedFriendlyName == 'toursAllowed') > -1;
    }

    isWelcomeTourAllowed(): boolean {
        return this.isUserPreferencesReceived && (this.completedInteractiveTours.length == 0 || this.completedInteractiveTours.findIndex((preferenceItem: PreferenceItem) => preferenceItem.selectedId == 'welcomeTour') == -1);
    }

    startWelcomeTour() {
        if (this.isInteractiveTourNotCompleted('welcomeTour') && this.isWelcomeTourAllowed()) {
            this.openWelcomeDialog.next(true);
        } else {
            this.isTourActive = false;
        }
    }

    public saveDecisionAndStartFirstTour(decision: string = 'toursForbidden'): void {
        this.saveCompletedTour('welcomeTour', decision);
        this.isTourActive = false;

        if (decision == 'toursAllowed' && this.authenticatedUserService.isVendor(null)) {
            this.start('vendorHomePage');
        } else if (decision == 'toursAllowed' && this.authenticatedUserService.isProvider(null)) {
            this.start('providerHomePage');
        }
    }

    public toggleToursPermit(toursAllowed: boolean): void {
        this.saveCompletedTour('welcomeTour', toursAllowed ? 'toursAllowed' : 'toursForbidden');
    }

    public startTourByName(tourName: string, forcedStart: boolean = false): void {
        if (tourName && this.isInteractiveTourExist(tourName) && (this.isInteractiveTourNotCompleted(tourName) || forcedStart)) {
            setTimeout(() => {
                this.driver = new Driver(this.driverOptions);
                this.setInteractiveTourSteps(tourName);
                if (this.steps.length > 0) {
                    this.driver.defineSteps(this.steps);
                    this.closeDialogsIfNeeded(tourName);
                    this.driver.start();
                    if (!forcedStart) {
                        this.saveCompletedTour(tourName);
                    }
                } else {
                    this.isTourActive = false;
                }
            }, 0);
        } else {
            this.isTourActive = false;
        }
    }

    public stop(): void {
        if (this.driver.isActivated) {
            this.driver.reset(true);
        }
    }

    public closeDialogsIfNeeded(tourName?: string) {
        if (tourName !== 'orgsMappingPage' && !this.router.url.includes('admin/a/')) {
            this.matDialog.closeAll();
            const overlayBackdropElements: NodeListOf<Element> = document.querySelectorAll('.cdk-overlay-backdrop.cdk-overlay-backdrop-showing, .mat-drawer-backdrop.mat-drawer-shown');
            if (overlayBackdropElements && overlayBackdropElements.length > 0) {
                overlayBackdropElements.forEach(element => element.dispatchEvent(new Event('click')));
            }
        }
    }

    public startCurrentPageTour(tourName: string): void {
        this.start(tourName, true);
    }

    public setCurrentPageTour(tourName: string): void {
        this.currentPageTour.next(tourName);
    }

    public isActivated(): boolean {
        return this.driver.isActivated;
    }

    public isInteractiveTourExist(tourName: string): boolean {
        return !!this.getTourByName(tourName);
    }

    public isInteractiveTourNotCompleted(tourName: string): boolean {
        return this.completedInteractiveTours.length == 0 || this.completedInteractiveTours.findIndex((preferenceItem: PreferenceItem) => preferenceItem.selectedId == tourName) < 0;
    }

    private checkIsStepExistInDOM(step: object): boolean {
        return step && step['element'] && document.querySelector(step['element']);
    }

    private setInteractiveTourSteps(tourName: string): void {
        this.steps = this.getTourByName(tourName).filter(step => this.checkIsStepExistInDOM(step));
    }

    private saveCompletedTour(tourName: string, decision: string = ''): void {
        const newUserPreferenceItem = new PreferenceItem(decision, tourName);
        const tourIndex = this.completedInteractiveTours.findIndex(tour => tour.selectedId === newUserPreferenceItem.selectedId);

        if (this.completedInteractiveTours && tourIndex < 0) {
            this.completedInteractiveTours.push(newUserPreferenceItem);
        } else {
            this.completedInteractiveTours[tourIndex] = newUserPreferenceItem;
        }

        const preferences = new Preferences();
        preferences.completedInteractiveTours = this.completedInteractiveTours.filter(preference => preference.selectedId !== '');
        this.completedInteractiveTours = preferences.completedInteractiveTours;

        this.store.dispatch(new AuthenticatedUpdateExtraUserPreferencesAction({preferences: preferences}));
    }

    private getDefaultInteractiveTours(): Observable<Object> {
        if (this.authenticatedUserService.isVendor(null) ) {
            return this.getVendorInteractiveTours();
        } else if (this.authenticatedUserService.isProvider(null)) {
            return this.getProviderInteractiveTours();
        }
    }

    private getTourByName(tourName: string): Array<Object> {
        return this.interactiveTours[tourName];
    }

    private getVendorInteractiveTours(): Observable<Object>  {
        return this.http.get('./assets/interactive-tours/vendor-interactive-tours.json')
            .pipe(
                map((tours: Object) => this.filterToursVisibility(tours, VendorToursVisibility))
            );
    }

    private getProviderInteractiveTours(): Observable<Object> {
        return this.http.get('./assets/interactive-tours/provider-interactive-tours.json')
            .pipe(
                switchMap((tours: Object) => {
                    if (this.IS_AMPLIFY_CUSTOMER) {
                        return this.http.get('./assets/interactive-tours/amplify-replacement-provider-steps.json')
                            .pipe(map((amplifyTours: Object) => this.replaceSteps(tours, amplifyTours)));
                    } else {
                        return observableOf(tours);
                    }
                }),
                map((tours: Object) => this.filterToursVisibility(tours, ProviderToursVisibility))
            );
    }

    private filterToursVisibility(tours: Object, ToursVisibility: Map<string, Map<number, boolean>>): Object {
        Object.keys(tours).forEach((tourId: string) => {
            if (tours[tourId] && ToursVisibility.has(tourId)) {
                const tourVisibility = ToursVisibility.get(tourId);

                tours[tourId] = tours[tourId].filter((tourItem: Object, index: number) =>
                    !tourVisibility.has(index) || tourVisibility.get(index)
                );
            }
        });
        return tours;
    }

    private replaceSteps(baseTour: Object, replacementTourSteps: Object): Object {
        const newTour = {};

        Object.keys(baseTour).forEach((page: string) => {
            newTour[page] = baseTour[page].map((step: Object) => {
                const replacedStep = (replacementTourSteps[page] || []).find((extraStep: Object) => extraStep['element'] == step['element']);

                if (replacedStep) {
                    return replacedStep;
                } else {
                    return step;
                }
            });
        });

        return newTour;
    }
}

export interface ITour {
    tourName: string;
    forcedStart?: boolean;
}
